<template>
  <v-card>
    <v-card-title>Добавления шаблона карты динамического наблюдения</v-card-title>

    <v-card-text>
      <v-row>
        <v-col>
          <v-card-title class="text-left pl-0">Системные</v-card-title>
          <v-select
            label="Выберите карту динамического наблюдения"
            item-text="name"
            item-value="id"
            return-object
            v-model="selectTemplate"
            :loading="templateListLoading"
            :items="templateList"
            @change="loadTemplate"
          />
        </v-col>
      </v-row>
    </v-card-text>

    <div v-if="selectTemplate">
      <Loader v-if="templateLoading" loading />
      <template>
        <v-card-text class="text-left">
          <v-list>
            <v-list-item>
              <v-list-item-content>
                <v-textarea v-if="editTitle" auto-grow rows="1" v-model="templateTitle"></v-textarea>
                <div v-else>{{ templateTitle }}</div>
              </v-list-item-content>
              <v-list-item-icon>
                <v-btn fab dark small color="primary" @click="editTitle = !editTitle">
                  <v-icon v-if="editTitle">mdi-content-save</v-icon>
                  <v-icon v-else>mdi-pencil</v-icon>
                </v-btn>
              </v-list-item-icon>
            </v-list-item>
          </v-list>
        </v-card-text>

        <v-card-text>
          <PatientDiseasesList label="Массовое назначение заболевания" return-object v-model="commonDisease" />
        </v-card-text>

        <v-card-text>
          <form id="form-template-data" @submit.prevent="submit">
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">
                      Названия
                    </th>
                    <th class="text-left">
                      Заболевание
                    </th>
                    <th class="text-left">
                      Когда?
                    </th>
                    <th class="text-center">
                      Критичность
                    </th>
                    <th class="text-center">
                      <div>Ограничить сбор</div>
                    </th>
                    <th class="text-center">
                      Начало задания
                    </th>
                    <th class="text-center">
                      Конец задания
                    </th>
                    <th class="text-center">
                      Пожизненно?
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(group, idx) in templateData">
                    <!-- Названия группы -->
                    <tr :key="`template-data-group-${idx}`">
                      <th colspan="9">
                        {{ group.name }}

                        <v-btn x-small color="primary" class="ml-5" @click="addRow(idx)">
                          <v-icon>
                            mdi-plus
                          </v-icon>
                        </v-btn>
                      </th>
                    </tr>

                    <template v-for="(value, row_index) in group.items">
                      <component v-bind:is="'TaskItemRow'" :key="value.uuid" :edit-mode="true" :form="value" :type="group">
                        <template #after>
                          <td>
                            <div class="d-flex flex-row justify-center align-center">
                              <v-btn title="Удалить" icon small text @click="removeRow(idx, row_index)">
                                <v-icon>mdi-delete</v-icon>
                              </v-btn>
                            </div>
                          </td>
                        </template>
                      </component>
                    </template>
                  </template>
                </tbody>
              </template>
            </v-simple-table>
          </form>
        </v-card-text>
      </template>

      <v-card-text v-if="validationError">
        <ValidationError :data="validationError" />
      </v-card-text>

      <v-card-actions class="d-flex justify-space-between">
        <v-btn type="submit" color="primary" form="form-template-data" :disabled="templateLoading" :loading="isSubmitting">
          <v-icon left>mdi-check</v-icon>
          Применить шаблон
        </v-btn>

        <v-btn disabled class="mr-0">
          <v-icon left>mdi-content-save</v-icon>
          Сохранить шаблон
        </v-btn>
      </v-card-actions>
    </div>
  </v-card>
</template>

<script>
import DatePicker from "@/components/forms/DatePicker.vue";
import TimePicker from "@/components/forms/TimePicker.vue";
import Modal from "@/components/Modal.vue";
import { groupBy } from "@/services/utils.js";
import Loader from "@/components/Loader.vue";
import AutocompleteMedParams from "@/components/AutocompleteMedParams.vue";
import { TasksRepository } from "@/modules/tasks/tasks.repository.js";
import ValidationError from "@/core/api-validation/ValidationError.vue";
import { required } from "vuelidate/lib/validators";
import { TaskGroups } from "@/modules/tasks/tasks.config.js";
import PatientDiseasesList from "@/components/PatientDiseasesList.vue";
import TaskItemRow from "@/views/pages/tasks/components/TaskItemRow.vue";

export default {
  name: "TaskTemplatesList",
  components: { PatientDiseasesList, ValidationError, AutocompleteMedParams, Loader, TimePicker, DatePicker, Modal, TaskItemRow },

  data() {
    return {
      isSubmitting: false,
      selectTemplate: null,
      templateTitle: "",
      editTitle: false,

      templateListLoading: true,
      templateList: [],

      durationList: [],
      periodicityList: [],
      template: [],
      templateLoading: false,
      templateData: [],
      validationError: null,
      commonDisease: null,
    };
  },

  computed: {
    periodicityListByKey() {
      const result = {};
      for (const item of this.periodicityList) {
        result[item.id] = item;
      }

      return result;
    },
  },

  validations: {
    templateData: {
      $each: {
        items: {
          $each: {
            name: { required },
          },
        },
      },
    },
  },

  watch: {
    selectTemplate(newVal) {
      this.templateTitle = newVal.name;
    },

    commonDisease(newVal) {
      for (const group of this.templateData) {
        if (!group.items.length) {
          continue;
        }

        for (const item of group.items) {
          item.diseases = newVal;
        }
      }
    },
  },

  async created() {
    await Promise.all([this.loadDurationList(), this.loadPeriodicityList(), this.loadTemplates()]);
  },

  methods: {
    async loadDurationList() {
      try {
        this.durationList = await TasksRepository.duration();
      } catch (e) {
        console.error(e);
        this.$dialog.notify.error("Ошибка загрузки списка 'длительности периодов'");
      }
    },

    async loadPeriodicityList() {
      try {
        this.periodicityList = await TasksRepository.periodicity();
      } catch (e) {
        console.error(e);
        this.$dialog.notify.error("Ошибка загрузки списка 'переодичностей'");
      }
    },

    async loadTemplates() {
      try {
        this.templateListLoading = true;
        const { data } = await this.$http.get("v1/events/templates");
        this.templateList = data.data ?? [];
      } catch (e) {
        console.error(e);
        this.$dialog.notify.error("Ошибка загрузки списка шаблонов");
      } finally {
        this.templateListLoading = false;
      }
    },

    /**
     * @param {object} template
     * @returns {Promise<void>}
     */
    async loadTemplate(template) {
      try {
        this.templateLoading = true;
        const { data } = await this.$http.get(`v1/events/templates/${template.id}`);
        this.template = data.data;

        if (this.template.length) {
          const types = groupBy("entity_type")(this.template);
          const result = [];

          for (const typeKey in types) {
            result.push({
              code: typeKey,
              name: this.$i18n.t(`tasks.types.${typeKey}`),
              items: types[typeKey].map(groupItem => {
                const entityPayload = JSON.parse(groupItem.entity_payload);
                let entity = null;

                // TODO: вынести в общий сервис
                if ([TaskGroups.param, TaskGroups.symptom].includes(typeKey)) {
                  entity = {
                    id: +groupItem.entity_id,
                    name: entityPayload.title,
                  };
                }

                return {
                  uuid: this.$utils.uuid(),
                  isTemplate: true,
                  authorId: this.$store.state.auth.id,
                  group: null,
                  name: entityPayload.title,
                  entity: entity,
                  entityId: groupItem.entity_id,
                  periodicity: this.periodicityListByKey[groupItem.event_periodicity_id],
                  startAt: groupItem.start_at ? this.$date(groupItem.start_at).format("YYYY-MM-DD") : "",
                  endAt: groupItem.end_at ? this.$date(groupItem.end_at).format("YYYY-MM-DD") : "",
                  diseases: Array.isArray(entityPayload.diseases) && entityPayload.diseases[0] ? entityPayload.diseases[0] : null,
                  critical: entityPayload?.fields?.critical || false,
                  limited: entityPayload?.fields?.limited || false,
                  lifelong: entityPayload?.fields?.lifelong || false,
                  finished: false,
                  order: 0,
                  factor: null,
                  comment: "",
                  list: [],
                };
              }),
            });
          }

          this.templateData = result;
        }
      } catch (e) {
        console.error(e);
        this.$dialog.notify.error("Ошибка загрузки шаблона");
      } finally {
        this.templateLoading = false;
      }
    },

    removeRow(index, row_index) {
      this.templateData[index].items.splice(row_index, 1);
      return true;
    },

    addRow(index) {
      const row = {
        uuid: this.$utils.uuid(),
        isTemplate: false,
        authorId: this.$store.state.auth.id,
        group: null,
        name: "",
        entity: null,
        entityId: null,
        periodicity: null,
        startAt: this.$date().format("YYYY-MM-DD"),
        endAt: "",
        diseases: null,
        critical: false,
        limited: false,
        lifelong: false,
        finished: false,
        order: 0,
        factor: null,
        comment: "",
        list: [],
      };

      this.templateData[index].items.push(row);
    },

    async submit() {
      this.validationError = null;

      // this.$v.templateData.$touch();
      // if (this.$v.templateData.$pending || this.$v.templateData.$error) return;

      const data = {
        title: this.templateTitle,
        events: [],
      };

      for (const group of this.templateData) {
        for (const item of group.items) {
          const payload = {
            title: item.entity ? item.entity.name : item.name,
            diseases: [],
            ui_input_type: "float",
            fields: {
              critical: item.critical,
              limited: item.limited,
              lifelong: item.lifelong,
            },
          };

          if (item.diseases) {
            payload.diseases.push(item.diseases);
          }

          if (item.entity?.measure_id) {
            payload.measure_id = item.entity?.measure_id;
          }

          if (item.entity?.type) {
            payload.ui_input_type = item.entity?.type;
          }

          data.events.push({
            entity_type: group.code,
            entity_id: item.entity ? item.entity.id : item.entityId,
            entity_payload: JSON.stringify(payload),
            event_periodicity_id: item.periodicity?.id || null,
            event_duration_id: null,
            factor: item.factor,
            order: item.order,
            start_at: item.startAt,
            end_at: item.endAt,
          });
        }
      }

      try {
        this.isSubmitting = true;
        await TasksRepository.addGroup(this.$store.state.customer.id, data);
        this.$dialog.notify.success("Карта наблюдения успешно добавлена");
        this.$bus.$emit("addNewTaskGroup");
        this.$emit("submit");
      } catch (e) {
        if (e?.response?.status === 422) {
          this.validationError = e.response.data;
          this.$dialog.notify.warning(this.$i18n.t(e.response.data.message));
        } else {
          this.$dialog.notify.error("Ошибка применения шаблона");
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped></style>
